import {
  CheckCircleIcon,
  MinusCircleIcon,
  WarningCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  answerCoverage,
  answerStatusKey,
  contradictionKey,
  contradictionOf,
  latestAnswers,
  questionKey,
  rejectedAnswers,
  rejectReasonKey,
  type CheckLevel,
  type DealerAnswer,
  type NegotiationDetail,
} from '@entities/negotiation';
import { Badge } from '@shared/ui';

import styles from './CheckAnswers.module.css';

interface CheckAnswersProps {
  detail: NegotiationDetail;
}

/**
 * Блок проверки: вопросы автохаусу и его ответы (FE-230, MVP_PRD §36.4).
 *
 * **Это сбор утверждений продавца, а не проверка**, и весь блок построен
 * вокруг одного правила: пользователь обязан отличать слова автохауса от слов
 * CarViQ.DE. Отсюда четыре решения разметки, ни одно из которых не косметическое:
 *
 * * **ответ показывается цитатой в изолированном блоке** с подписью «со слов
 *   автохауса, CarViQ.DE не проверял». Пересказа нет: поле без дословной цитаты
 *   на сервере не заполняется вовсе (§36.4), и придумывать его здесь тем
 *   более нечем;
 * * **отказ и молчание — разные состояния, и оба названы.** «Автохаус
 *   отказался отвечать» рядом с прямой оговоркой, что это данные, а не
 *   приговор автомобилю; «не ответил» — с оговоркой, что автомобиль остаётся
 *   в сравнении (§36.2);
 * * **расхождение показывается обеими цитатами без интерпретации** (§36.5).
 *   Две колонки равного веса, «написано раньше» и «написано позже», и ни
 *   одного слова о причине: CarViQ.DE показывает факт и молчит;
 * * **VIN — это ответ автохауса, а не проверка.** Рядом сказано прямо, что
 *   CarViQ.DE не обращается к VIN-сервисам и ничего по номеру не устанавливает.
 *   Кнопки «проверить VIN» и ссылок на сторонние сервисы нет и быть не может
 *   (CLAUDE.md §2).
 *
 * Компонент ничего не решает: уровень, состояние и расхождение приходят с
 * сервера кодами, а экран раскладывает их по ключам i18n.
 */
export function CheckAnswers({ detail }: CheckAnswersProps) {
  const { t } = useTranslation();

  const levels: CheckLevel[] = ['control_check', 'deep_check'];
  const sections = levels
    .map((level) => ({ level, rows: latestAnswers(detail.check_answers, level) }))
    .filter((section) => section.rows.length > 0);

  // Пустого блока «спросили и не получили ответа» там, где не спрашивали, быть
  // не должно: это была бы неправда (§36.1). До первого письма блока нет.
  if (sections.length === 0) return null;

  return (
    <section className={styles.wrap} aria-label={t('negotiation.check.title')}>
      <h2 className={styles.title}>{t('negotiation.check.title')}</h2>
      {sections.map(({ level, rows }) => (
        <LevelSection key={level} level={level} rows={rows} />
      ))}
    </section>
  );
}

function LevelSection({ level, rows }: { level: CheckLevel; rows: DealerAnswer[] }) {
  const { t } = useTranslation();
  const coverage = answerCoverage(rows);
  const suffix = level === 'control_check' ? 'control' : 'deep';

  return (
    <section className={styles.level}>
      <h3 className={styles.levelTitle}>{t(`negotiation.check.${suffix}Title`)}</h3>
      <p className={styles.hint}>{t(`negotiation.check.${suffix}Hint`)}</p>
      {/* «3 из 6» — из данных, а не из литерала: число заданных вопросов
          зависит от автомобиля (§36.3). */}
      <p className={styles.coverage}>{t('negotiation.check.coverage', coverage)}</p>
      <ol className={styles.list}>
        {rows.map((answer) => (
          <li key={answer.answer_id} className={styles.item}>
            <AnswerRow answer={answer} />
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Тон и иконка бейджа состояния — семантическая шкала DESIGN_SYSTEM §2.4.
 * Цвет — не единственный носитель смысла: иконка и подпись стоят внутри
 * самого бейджа (CLAUDE.md §8).
 *
 * `refused` намеренно не красный: отказ отвечать — это данные, а не
 * нарушение, и красным он выглядел бы приговором (§36.4).
 */
function badgeOf(status: string): {
  tone: 'match' | 'compromise' | 'attention' | 'nodata';
  icon: ReactNode;
} {
  switch (status) {
    case 'answered':
      return { tone: 'match', icon: <CheckCircleIcon weight="bold" /> };
    case 'refused':
      return { tone: 'compromise', icon: <WarningIcon weight="bold" /> };
    case 'unclear':
      return { tone: 'attention', icon: <WarningCircleIcon weight="bold" /> };
    default:
      return { tone: 'nodata', icon: <MinusCircleIcon weight="bold" /> };
  }
}

function AnswerRow({ answer }: { answer: DealerAnswer }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const contradiction = contradictionOf(answer);
  const rejected = rejectedAnswers(answer);
  const isVin = answer.question_code === 'ASK_VIN_FOR_BUYER' && answer.quote !== null;
  const badge = badgeOf(answer.status);

  return (
    <>
      <p className={styles.head}>
        <span className={styles.question}>{t(questionKey(answer.question_code))}</span>
        <Badge tone={badge.tone} icon={badge.icon} uppercase={false}>
          {t(answerStatusKey(answer.status))}
        </Badge>
      </p>

      {/* Цитата — единственная форма, в которой существует ответ автохауса.
          Ссылки в ней не разбираются: текст продавца недоверенный (§22.8). */}
      {answer.quote === null ? (
        // «Нет данных» стоит там же, где стояла бы цитата: пустое место на
        // экране читается как «не успели показать», а не как «сведений нет»
        // (DESIGN_SYSTEM §2.4).
        <p className={styles.noData}>{t('negotiation.check.noData')}</p>
      ) : (
        <figure className={isVin ? styles.vin : styles.quoteBlock}>
          {isVin ? (
            <figcaption className={styles.vinTitle}>{t('negotiation.check.vinTitle')}</figcaption>
          ) : null}
          <blockquote className={styles.quote}>{answer.quote}</blockquote>
          <figcaption className={styles.quoteNote}>{t('negotiation.check.quoteNote')}</figcaption>
        </figure>
      )}

      {/* «Проверьте его самостоятельно»: CarViQ.DE по номеру ничего не
          устанавливает и никуда не обращается (§36.3). */}
      {isVin ? <p className={styles.vinNote}>{t('negotiation.check.vinNote')}</p> : null}

      {answer.status === 'refused' ? (
        <p className={styles.note}>{t('negotiation.check.refusedNote')}</p>
      ) : null}
      {answer.status === 'unclear' ? (
        <p className={styles.note}>{t('negotiation.check.unclearNote')}</p>
      ) : null}
      {answer.status === 'unanswered' ? (
        <p className={styles.note}>{t('negotiation.check.silence')}</p>
      ) : null}

      {/* Дата у ответа и у неотвеченного вопроса значит разное, и подписи
          разные. Строка неотвеченного вопроса создаётся в момент отправки
          письма, поэтому её `created_at` — это «когда спросили». */}
      <p className={styles.time}>
        {t(answer.quote === null ? 'negotiation.check.askedAt' : 'negotiation.check.answeredAt', {
          date: formatDate(answer.created_at, locale),
        })}
      </p>

      {contradiction === null ? null : (
        <section
          className={styles.discrepancy}
          aria-label={t('negotiation.check.discrepancyTitle')}
        >
          <p className={styles.discrepancyTitle}>
            <WarningCircleIcon size={16} aria-hidden="true" className={styles.discrepancyIcon} />
            <span>
              {t('negotiation.check.discrepancyTitle')}
              {' · '}
              {t(contradictionKey(contradiction.code))}
            </span>
          </p>
          {/* Две колонки равного веса. Ни одна не помечена «правильной»:
              CarViQ.DE не знает, какая из них верна, и не делает вид, что знает. */}
          <div className={styles.sides}>
            {(['previous', 'current'] as const).map((side) => (
              <figure key={side} className={styles.side}>
                <figcaption className={styles.sideHead}>
                  {t(
                    side === 'previous'
                      ? 'negotiation.check.discrepancyEarlier'
                      : 'negotiation.check.discrepancyLater',
                  )}
                  {' · '}
                  {formatDate(contradiction[side].at, locale)}
                </figcaption>
                <blockquote className={styles.quote}>{contradiction[side].quote}</blockquote>
              </figure>
            ))}
          </div>
          <p className={styles.note}>{t('negotiation.check.discrepancyNote')}</p>
        </section>
      )}

      {rejected.length === 0 ? null : (
        <details className={styles.rejected}>
          <summary className={styles.rejectedSummary}>
            {t('negotiation.check.rejectedTitle')}
          </summary>
          <p className={styles.note}>{t('negotiation.check.rejectedNote')}</p>
          <ul className={styles.rejectedList}>
            {rejected.map((item) => (
              <li key={`${item.field}-${item.reason}`}>
                {t(questionKey(item.field))}
                {' — '}
                {t(rejectReasonKey(item.reason))}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function formatDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(value));
}
